import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, Divider, IconButton, Typography } from '@mui/material';
import OrganisationTreeNode from 'shared/features/organisation/models/organisation-tree-node';
import { OrganisationUnitTreeProvider } from 'shared/features/organisation/providers/organisation-unit-tree-provider';

import EmergencyAlarmRouteOrganisationUnit from 'features/organisation/domain/models/emergency-alarm-route-organisation-unit';
import MultiSelectEmergencyAlarmRouteTree from '../../../multi-select-alternative-diversion-tree/multi-select-alternative-diversion-tree.component';
import styles from './organisation-details-emergency-alarm-route-modal.module.scss';

type Props = {
  organisationUnitId: string;
  isOpen: boolean;
  selectedEmergencyAlarmRouteUnits: EmergencyAlarmRouteOrganisationUnit[];
  onClose: () => void;
  onSaveButtonClicked: (selectedEmergencyAlarmRouteUnits: EmergencyAlarmRouteOrganisationUnit[]) => void;
};

const OrganisationDetailsEmergencyAlarmRouteModal = (props: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('organisation');

  const [selectedEmergencyAlarmRouteUnits, setSelectedEmergencyAlarmRouteUnits] = useState<EmergencyAlarmRouteOrganisationUnit[]>(
    props.selectedEmergencyAlarmRouteUnits,
  );

  useEffect(() => {
    setSelectedEmergencyAlarmRouteUnits(props.selectedEmergencyAlarmRouteUnits);
  }, [props.selectedEmergencyAlarmRouteUnits]);

  const handleOnClose = (): void => {
    props.onClose();
    setSelectedEmergencyAlarmRouteUnits(props.selectedEmergencyAlarmRouteUnits);
  };

  const handleOnSaveButtonClicked = (): void => {
    props.onSaveButtonClicked(selectedEmergencyAlarmRouteUnits);
  };

  const getBreadCrumbs = (node: OrganisationTreeNode): string[] => {
    const breadCrumbs: string[] = [];
    let currentNode: OrganisationTreeNode | undefined = node;

    while (currentNode) {
      breadCrumbs.unshift(currentNode.name);
      currentNode = currentNode.parentOrganisationUnit;
    }

    return breadCrumbs;
  };

  const onSelectOrganisationUnit = (selectedNode: OrganisationTreeNode): void => {
    const selectedOrganisationUnitsCopy = [...selectedEmergencyAlarmRouteUnits];
    const indexOfNode = selectedOrganisationUnitsCopy.findIndex((x) => x.id === selectedNode.id);

    const selectedEmergencyAlarmRoute: EmergencyAlarmRouteOrganisationUnit = {
      id: selectedNode.id,
      name: selectedNode.name,
      breadCrumbs: getBreadCrumbs(selectedNode),
    };

    if (indexOfNode === -1) {
      selectedOrganisationUnitsCopy.push(selectedEmergencyAlarmRoute);
    } else {
      selectedOrganisationUnitsCopy.splice(indexOfNode, 1);
    }

    removeChildrenFromSelectedOrganisationUnits(selectedOrganisationUnitsCopy, selectedNode.childOrganisationUnits);

    setSelectedEmergencyAlarmRouteUnits(selectedOrganisationUnitsCopy);
  };

  const removeChildrenFromSelectedOrganisationUnits = (
    selectedOrganisationUnitsCopy: EmergencyAlarmRouteOrganisationUnit[],
    childOrganisationUnits: OrganisationTreeNode[],
  ): void => {
    childOrganisationUnits.forEach((child) => {
      const indexOfNode = selectedOrganisationUnitsCopy.findIndex((x) => x.id === child.id);

      if (indexOfNode !== -1) {
        selectedOrganisationUnitsCopy.splice(indexOfNode, 1);
      }

      removeChildrenFromSelectedOrganisationUnits(selectedOrganisationUnitsCopy, child.childOrganisationUnits);
    });
  };

  return (
    <Dialog
      className={styles.organisationDetailsLinkableEmergencyAlarmRouteModal}
      open={props.isOpen}
      onClose={handleOnClose}
      slotProps={{
        paper: {
          classes: { root: styles.linkableEmergencyAlarmRouteModalPaper },
        },
      }}
    >
      <div className={styles.linkableEmergencyAlarmRouteModalHeaderContainer}>
        <Typography variant="h2">{t('linkableEmergencyAlarmRouteModal.title')}</Typography>
        <div className={styles.actions}>
          {selectedEmergencyAlarmRouteUnits.length < 1 && (
            <Button variant="contained" disabled startIcon={<CheckIcon className={styles.checkIcon} />}>
              {t('linkableEmergencyAlarmRouteModal.saveButton')}
            </Button>
          )}
          {selectedEmergencyAlarmRouteUnits.length > 0 && (
            <Button
              className={styles.linkButton}
              variant="contained"
              onClick={handleOnSaveButtonClicked}
              startIcon={<CheckIcon className={styles.checkIcon} />}
              data-testid="linkselectedEmergencyAlarmRouteUnitsButton"
            >
              {t('linkableEmergencyAlarmRouteModal.saveButton')}
            </Button>
          )}

          <Divider orientation="vertical" flexItem className={styles.actionsDivider} />
          <IconButton aria-label="close" className={styles.closeButton} onClick={handleOnClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </div>
      <Divider className={styles.tableDivider} />
      <OrganisationUnitTreeProvider.ForLocation organisationUnitId={props.organisationUnitId}>
        <MultiSelectEmergencyAlarmRouteTree
          onSelectOrganisationUnit={onSelectOrganisationUnit}
          selectedOrganisationUnitIds={selectedEmergencyAlarmRouteUnits.map((x) => x.id)}
          disabledChildrenOfOrganisationUnitIds={[props.organisationUnitId]}
        />
      </OrganisationUnitTreeProvider.ForLocation>
    </Dialog>
  );
};

export default OrganisationDetailsEmergencyAlarmRouteModal;
